import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Layout, ErrorNotice } from '../components/Layout';
import { useMe } from '../components/AuthGuard';
import { api, googleLoginUrl } from '../lib/api';
import { MeetingTaxonomySettings } from '../components/MeetingTaxonomySettings';
import { DocumentTaxonomySettings } from '../components/DocumentTaxonomySettings';
import { ColorGroupSettings } from '../components/ColorGroupSettings';
import { CalendarDelegationSettings } from '../components/CalendarDelegationSettings';

export function SettingsPage() {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const disconnect = useMutation({
    mutationFn: () => api('/api/auth/disconnect-google', { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['me'] }),
  });
  const logout = useMutation({
    mutationFn: () => api('/api/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      queryClient.clear();
      navigate('/login');
    },
  });
  const prompts = useQuery({
    queryKey: ['appointment-prompts'],
    queryFn: () =>
      api<{ data: Array<{ templateType: string; prompt: string; isDefault: boolean }> }>(
        '/api/appointment-prompts',
      ).then((result) => result.data),
  });
  const [editingPrompt, setEditingPrompt] = useState<string | null>(null);
  const [promptText, setPromptText] = useState('');
  const savePrompt = useMutation({
    mutationFn: () =>
      api(`/api/appointment-prompts/${editingPrompt}`, {
        method: 'PUT',
        body: JSON.stringify({ prompt: promptText }),
      }),
    onSuccess: async () => {
      setEditingPrompt(null);
      await queryClient.invalidateQueries({ queryKey: ['appointment-prompts'] });
    },
  });
  const resetPrompt = useMutation({
    mutationFn: (templateType: string) =>
      api(`/api/appointment-prompts/${templateType}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['appointment-prompts'] });
    },
  });
  const templateNames: Record<string, string> = {
    meeting: 'นัดประชุม',
    task: 'สิ่งที่ต้องทำ',
    medical: 'นัดแพทย์',
    activity: 'กิจกรรม',
  };
  const groups = useQuery({
    queryKey: ['golf-groups'],
    queryFn: () =>
      api<{ data: Array<{ id: string; name: string; members: string[] }> }>(
        '/api/appointments/golf-groups',
      ).then((r) => r.data),
  });
  const [groupName, setGroupName] = useState('');
  const [members, setMembers] = useState('');
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const saveGroup = useMutation({
    mutationFn: () =>
      api(
        editingGroup
          ? `/api/appointments/golf-groups/${editingGroup}`
          : '/api/appointments/golf-groups',
        {
          method: editingGroup ? 'PATCH' : 'POST',
          body: JSON.stringify({
            name: groupName,
            members: members
              .split('\n')
              .map((x) => x.trim())
              .filter(Boolean),
          }),
        },
      ),
    onSuccess: () => {
      setGroupName('');
      setMembers('');
      setEditingGroup(null);
      void queryClient.invalidateQueries({ queryKey: ['golf-groups'] });
    },
  });
  const deleteGroup = useMutation({
    mutationFn: (id: string) => api(`/api/appointments/golf-groups/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['golf-groups'] }),
  });
  return (
    <Layout title="ตั้งค่าบัญชี">
      <section className="mx-auto max-w-lg space-y-4">
        <div className="card">
          <p className="text-sm text-slate-500">บัญชี</p>
          <h2 className="mt-1 font-semibold">{me.data?.displayName}</h2>
          <p className="text-sm text-slate-600">{me.data?.email}</p>
        </div>
        <div className="card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold">Google Calendar</h2>
              <p className="text-sm text-slate-500">
                {me.data?.googleConnected ? 'เชื่อมต่อแล้ว' : 'ไม่ได้เชื่อมต่อ'}
              </p>
            </div>
            <span className={me.data?.googleConnected ? 'text-emerald-600' : 'text-amber-600'}>
              {me.data?.googleConnected ? '●' : '○'}
            </span>
          </div>
          {me.data?.googleConnected ? (
            <button
              className="btn-secondary mt-4 w-full"
              onClick={() => disconnect.mutate()}
              disabled={disconnect.isPending}
            >
              ยกเลิกการเชื่อมต่อ Google
            </button>
          ) : (
            <a className="btn-primary mt-4 w-full" href={googleLoginUrl()}>
              เชื่อมต่อ Google ใหม่
            </a>
          )}
        </div>
        <CalendarDelegationSettings />
        <Link to="/privacy" className="card flex min-h-14 items-center justify-between font-medium">
          ความเป็นส่วนตัวและลบข้อมูล <span>›</span>
        </Link>
        <MeetingTaxonomySettings />
        <DocumentTaxonomySettings />
        <ColorGroupSettings />
        <div className="card space-y-3">
          <div>
            <h2 className="font-semibold">ก๊วนกอล์ฟ</h2>
            <p className="text-sm text-slate-500">
              สมาชิกจะถูกเติมให้อัตโนมัติเมื่อเลือกก๊วนในกิจกรรม Golf
            </p>
          </div>
          {groups.data?.map((group) => (
            <div key={group.id} className="rounded-xl border p-3">
              <strong>{group.name}</strong>
              <p className="mt-1 text-sm text-slate-600">
                {group.members.join(', ') || 'ยังไม่มีสมาชิก'}
              </p>
              <div className="mt-2 flex gap-3 text-sm">
                <button
                  className="font-semibold text-teal-700"
                  onClick={() => {
                    setEditingGroup(group.id);
                    setGroupName(group.name);
                    setMembers(group.members.join('\n'));
                  }}
                >
                  แก้ไข
                </button>
                <button
                  className="font-semibold text-red-700"
                  onClick={() => {
                    if (confirm(`ลบก๊วน ${group.name}?`)) deleteGroup.mutate(group.id);
                  }}
                >
                  ลบ
                </button>
              </div>
            </div>
          ))}
          <div className="rounded-xl bg-slate-50 p-3">
            <input
              className="field"
              placeholder="ชื่อก๊วน"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
            <textarea
              className="field mt-2 min-h-20"
              placeholder="สมาชิก หนึ่งคนต่อบรรทัด"
              value={members}
              onChange={(e) => setMembers(e.target.value)}
            />
            <div className="mt-2 flex gap-2">
              <button
                className="btn-primary flex-1"
                disabled={!groupName.trim() || saveGroup.isPending}
                onClick={() => saveGroup.mutate()}
              >
                {editingGroup ? 'บันทึกก๊วน' : '+ สร้างก๊วน'}
              </button>
              {editingGroup && (
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setEditingGroup(null);
                    setGroupName('');
                    setMembers('');
                  }}
                >
                  ยกเลิก
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="card space-y-3">
          <div>
            <h2 className="font-semibold">Prompt Mapping การนัดหมาย</h2>
            <p className="text-sm text-slate-500">
              ใช้ Prompt เดียวต่อ Template และแก้ไขได้เฉพาะบัญชีนี้
            </p>
          </div>
          {prompts.data?.map((item) => (
            <div key={item.templateType} className="rounded-xl border p-3">
              <div className="flex items-center justify-between gap-2">
                <strong>{templateNames[item.templateType] ?? item.templateType}</strong>
                <span className="text-xs text-slate-500">
                  {item.isDefault ? 'ค่าเริ่มต้น' : 'กำหนดเอง'}
                </span>
              </div>
              {editingPrompt === item.templateType ? (
                <>
                  <textarea
                    className="field mt-2 min-h-32"
                    value={promptText}
                    onChange={(event) => setPromptText(event.target.value)}
                  />
                  <div className="mt-2 flex gap-2">
                    <button className="btn-secondary flex-1" onClick={() => setEditingPrompt(null)}>
                      ยกเลิก
                    </button>
                    <button
                      className="btn-primary flex-1"
                      onClick={() => savePrompt.mutate()}
                      disabled={savePrompt.isPending}
                    >
                      บันทึก
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="mt-2 line-clamp-2 text-sm text-slate-600">{item.prompt}</p>
                  <div className="mt-2 flex gap-3 text-sm">
                    <button
                      className="font-semibold text-teal-700"
                      onClick={() => {
                        setEditingPrompt(item.templateType);
                        setPromptText(item.prompt);
                      }}
                    >
                      แก้ไข
                    </button>
                    {!item.isDefault && (
                      <button
                        className="font-semibold text-amber-700"
                        onClick={() => resetPrompt.mutate(item.templateType)}
                      >
                        คืนค่าเริ่มต้น
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
        {(disconnect.error || logout.error) && (
          <ErrorNotice
            message={(disconnect.error ?? logout.error)?.message ?? 'ดำเนินการไม่สำเร็จ'}
          />
        )}
        <button
          className="btn-secondary w-full"
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
        >
          ออกจากระบบ
        </button>
      </section>
    </Layout>
  );
}
